import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import {
  LayoutDashboard, ScanBarcode, Terminal, FolderOpen, History,
  LogOut, Globe, ScanLine, ChevronLeft, Menu, Settings, Cpu, Users,
  Sun, Moon, X, HardDrive, Wrench, Sparkles
} from 'lucide-react';
import { useState } from 'react';

export default function AppLayout() {
  const { t, i18n } = useTranslation();
  const { user, logout, switchLanguage } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = () => { logout(); navigate('/login'); };
  const isRTL = i18n.language === 'he';

  const navItems = [
    { to: '/', icon: LayoutDashboard, label: t('nav.dashboard'), end: true },
    { to: '/barcode', icon: ScanBarcode, label: t('nav.barcodeGenerator') },
    { to: '/serial', icon: Terminal, label: t('nav.serialConsole') },
    { to: '/firmware', icon: Cpu, label: isRTL ? 'עדכון קושחה' : 'Firmware' },
    { to: '/files', icon: HardDrive, label: isRTL ? 'קבצים' : 'Files' },
    { to: '/profiles', icon: FolderOpen, label: t('nav.profiles') },
    { to: '/history', icon: History, label: t('nav.history') },
    { to: '/settings', icon: Settings, label: t('nav.settings') },
    ...((user?.role === 'admin' || user?.aiAccess) ? [
      { to: '/ai', icon: Sparkles, label: isRTL ? 'עוזר AI' : 'AI Assistant' },
    ] : []),
    ...(user?.role === 'admin' ? [
      { to: '/firmware-builder', icon: Wrench, label: isRTL ? 'בניית קושחה' : 'Firmware Builder' },
      { to: '/admin/users', icon: Users, label: isRTL ? 'משתמשים' : 'Users' },
    ] : []),
  ];

  const SidebarContent = ({ isMobile }) => (
    <>
      {/* Logo */}
      <div className={`flex flex-col items-center px-4 py-3 border-b border-[var(--border)]`}>
        <img src="/logo.png" alt="SophistiScan" className={`${collapsed && !isMobile ? 'w-9 h-9 object-contain' : 'h-8'}`} />
        {(!collapsed || isMobile) && (
          <span className="block text-[10px] text-brand-500 tracking-[0.2em] uppercase mt-1">Config Tool</span>
        )}
        {isMobile && (
          <button onClick={() => setMobileOpen(false)} className="btn-ghost absolute top-2 left-2"><X className="w-5 h-5" /></button>
        )}
      </div>

      {/* Nav */}
      <nav className="flex-1 py-4 px-3 space-y-1 overflow-y-auto">
        {navItems.map(({ to, icon: Icon, label, end }) => (
          <NavLink key={to} to={to} end={end}
            onClick={() => isMobile && setMobileOpen(false)}
            className={({ isActive }) => `
              flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-150 border
              ${isActive ? 'text-brand-500 border-brand-500/20' : 'border-transparent'}
              ${collapsed && !isMobile ? 'justify-center' : ''}
            `}
            style={({ isActive }) => ({
              background: isActive ? 'var(--brand-light)' : 'transparent',
              color: isActive ? 'var(--brand)' : 'var(--text-secondary)',
            })}
            title={collapsed && !isMobile ? label : undefined}
          >
            <Icon className="w-5 h-5 shrink-0" />
            {(!collapsed || isMobile) && <span className="text-sm font-medium truncate">{label}</span>}
          </NavLink>
        ))}
      </nav>

      {/* Bottom */}
      <div className="p-3 border-t border-[var(--border)] space-y-1">
        {/* Theme toggle */}
        <button onClick={toggleTheme}
          className={`flex items-center gap-3 px-3 py-2 rounded-lg w-full transition-all ${collapsed && !isMobile ? 'justify-center' : ''}`}
          style={{ color: 'var(--text-secondary)' }}
        >
          {isDark ? <Sun className="w-5 h-5 shrink-0" /> : <Moon className="w-5 h-5 shrink-0" />}
          {(!collapsed || isMobile) && <span className="text-sm">{isDark ? (isRTL ? 'מצב בהיר' : 'Light Mode') : (isRTL ? 'מצב כהה' : 'Dark Mode')}</span>}
        </button>

        {/* Language */}
        <button onClick={() => switchLanguage(i18n.language === 'en' ? 'he' : 'en')}
          className={`flex items-center gap-3 px-3 py-2 rounded-lg w-full transition-all ${collapsed && !isMobile ? 'justify-center' : ''}`}
          style={{ color: 'var(--text-secondary)' }}
        >
          <Globe className="w-5 h-5 shrink-0" />
          {(!collapsed || isMobile) && <span className="text-sm">{i18n.language === 'en' ? 'עברית' : 'English'}</span>}
        </button>

        {/* Collapse (desktop only) */}
        {!isMobile && (
          <button onClick={() => setCollapsed(!collapsed)}
            className={`flex items-center gap-3 px-3 py-2 rounded-lg w-full transition-all ${collapsed ? 'justify-center' : ''}`}
            style={{ color: 'var(--text-secondary)' }}
          >
            {collapsed ? <Menu className="w-5 h-5" /> : <ChevronLeft className="w-5 h-5" />}
            {!collapsed && <span className="text-sm">{isRTL ? 'כווץ' : 'Collapse'}</span>}
          </button>
        )}

        {/* User */}
        <div className={`flex items-center gap-3 px-3 py-2 ${collapsed && !isMobile ? 'justify-center' : ''}`}>
          <div className="w-8 h-8 rounded-full bg-brand-600/20 border border-brand-500/30 flex items-center justify-center shrink-0">
            <span className="text-sm font-bold text-brand-500">{user?.displayName?.[0]?.toUpperCase() || '?'}</span>
          </div>
          {(!collapsed || isMobile) && (
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate" style={{color:'var(--text-primary)'}}>{user?.displayName}</p>
              <p className="text-xs truncate" style={{color:'var(--text-muted)'}}>{user?.email}</p>
            </div>
          )}
          <button onClick={handleLogout} className="shrink-0 hover:text-red-500 transition-colors" style={{color:'var(--text-muted)'}} title={t('nav.logout')}>
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </div>
    </>
  );

  return (
    <div className="flex h-screen overflow-hidden" dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Desktop Sidebar */}
      <aside className={`hidden md:flex flex-col border-r border-[var(--border)] transition-all duration-300 ${collapsed ? 'w-[72px]' : 'w-64'}`}
        style={{ background: 'var(--bg-sidebar)' }}>
        <SidebarContent isMobile={false} />
      </aside>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setMobileOpen(false)} />
          <aside className="absolute inset-y-0 right-0 w-72 flex flex-col shadow-2xl" style={{ background: 'var(--bg-sidebar)' }}>
            <SidebarContent isMobile={true} />
          </aside>
        </div>
      )}

      {/* Main */}
      <main className="flex-1 overflow-y-auto" style={{ background: 'var(--bg-primary)' }}>
        {/* Mobile top bar */}
        <div className="md:hidden flex items-center justify-between px-4 h-14 border-b border-[var(--border)]" style={{ background: 'var(--bg-sidebar)' }}>
          <img src="/logo.png" alt="SophistiScan" className="h-7" />
          <button onClick={() => setMobileOpen(true)} className="btn-ghost">
            <Menu className="w-5 h-5" />
          </button>
        </div>
        <Outlet />
      </main>
    </div>
  );
}
